import React from 'react';
import { AccountData, AccountStatus } from './types';

export const styleCache: Record<string, React.CSSProperties> = {};

export function s(css: string): React.CSSProperties {
  if (!css) return {};
  if (styleCache[css]) return styleCache[css];
  const obj: Record<string, string> = {};
  const decls = css.split(';');
  for (let i = 0; i < decls.length; i++) {
    const colon = decls[i].indexOf(':');
    if (colon === -1) continue;
    const prop = decls[i].slice(0, colon).trim();
    const val = decls[i].slice(colon + 1).trim();
    if (!prop || !val) continue;
    const camel = prop.startsWith('--') ? prop : prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  }
  styleCache[css] = obj;
  return obj;
}

export function money(n: number, dec?: boolean): string {
  return (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: dec ? 2 : 0,
    maximumFractionDigits: dec ? 2 : 0
  });
}

export function compact(n: number): string {
  return n >= 1e6 ? '$' + (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? '$' + (n / 1e3).toFixed(1) + 'K' : '$' + Math.round(n);
}

export function formatDate(t: number): string {
  return new Date(t).toLocaleDateString('en-GB');
}

export function vp(a: AccountData, end: number) {
  return a.payouts.filter(p => p.verified && p.date <= end);
}

export function consistent(a: AccountData, end: number) {
  return a.stage === 'Funded' && vp(a, end).length >= 2 && a.consistencyScore >= 75;
}

export function statusOf(a: AccountData): AccountStatus {
  if (a.paused) return 'Paused';
  if (a.drawdown <= -8) return 'Recovery';
  if (a.drawdown <= -5 || a.riskCompliance < 85) return 'Attention';
  if (consistent(a, Infinity) && a.consistencyScore >= 85 && a.drawdown > -3 && a.riskCompliance >= 90) return 'Scale Ready';
  return 'Normal';
}

export const REACH: Record<string, (a: AccountData, end: number) => boolean> = {
  Developing: () => true,
  Challenge: a => ['Challenge', 'Verified', 'Funded'].includes(a.stage),
  Verified: a => ['Verified', 'Funded'].includes(a.stage),
  Funded: a => a.stage === 'Funded',
  Payout: (a, e) => a.stage === 'Funded' && vp(a, e).length > 0,
  Consistent: (a, e) => consistent(a, e),
  'Scale Ready': a => statusOf(a) === 'Scale Ready'
};

// Risk score (0–100) computed from an account's closed trades against the prop-firm limits.
// The API's own "risk" field is 0 for most accounts regardless of how they trade, so we rebuild it.
export const RISK_LIMITS = {
  maxLossPct: 10, // max drawdown allowed, % of initial capital
  dailyLossPct: 5, // max loss in one day, % of initial capital
  tradeLossPct: 1 // a single losing trade above this, % of initial capital, breaks risk-per-trade
};
export const RISK_WEIGHTS = { drawdown: 0.4, daily: 0.3, perTrade: 0.3 };

export interface RiskCalc {
  score: number;
  maxDrawdownPct: number;
  worstDayPct: number;
  oversizedLosses: number;
  losingTrades: number;
  trades: number;
}

const clampPct = (v: number) => Math.max(0, Math.min(100, v));

export function riskFromReport(rep: any): RiskCalc | null {
  if (!rep) return null;
  const init = Number(rep.account_info?.initial_balance ?? rep.initial_balance);
  const raw: any[] = rep.raw_trades || rep.journal_closed_trades || rep.closed_trades || rep.trades || [];
  if (!init || !Array.isArray(raw) || raw.length === 0) return null;

  const trades = raw
    .map(t => ({
      time: String(t.close_time || t.open_time || ''),
      pnl: Number(t.net_profit ?? t.profit ?? 0),
      balanceAfter: t.balance_after !== undefined && t.balance_after !== null ? Number(t.balance_after) : NaN
    }))
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

  // Peak-to-trough drop of the closed-trade balance curve (plus current equity for open positions)
  let bal = init;
  let peak = init;
  let maxDd = 0;
  const visit = (b: number) => {
    if (b > peak) peak = b;
    maxDd = Math.max(maxDd, peak - b);
  };
  trades.forEach(t => {
    bal = isNaN(t.balanceAfter) ? bal + t.pnl : t.balanceAfter;
    visit(bal);
  });
  const equity = Number(rep.current_equity);
  if (!isNaN(equity) && equity > 0) visit(equity);
  const maxDrawdownPct = (maxDd / init) * 100;

  // Worst single day
  let dayResults: number[] = [];
  if (Array.isArray(rep.daily_summary) && rep.daily_summary.length > 0) {
    dayResults = rep.daily_summary.map((d: any) => Number(d.result ?? d.net_profit ?? d.profit ?? 0));
  } else {
    const byDay: Record<string, number> = {};
    trades.forEach(t => {
      const day = t.time.slice(0, 10);
      byDay[day] = (byDay[day] || 0) + t.pnl;
    });
    dayResults = Object.values(byDay);
  }
  const worstDayPct = (Math.max(0, -Math.min(0, ...dayResults)) / init) * 100;

  // Losing trades bigger than the per-trade limit
  const losers = trades.filter(t => t.pnl < 0);
  const oversizedLosses = losers.filter(t => -t.pnl > (init * RISK_LIMITS.tradeLossPct) / 100).length;

  const ddScore = clampPct(100 * (1 - maxDrawdownPct / RISK_LIMITS.maxLossPct));
  const dayScore = clampPct(100 * (1 - worstDayPct / RISK_LIMITS.dailyLossPct));
  const tradeScore = losers.length ? clampPct(100 * (1 - oversizedLosses / losers.length)) : 100;
  const score = Math.round(
    ddScore * RISK_WEIGHTS.drawdown + dayScore * RISK_WEIGHTS.daily + tradeScore * RISK_WEIGHTS.perTrade
  );

  return { score, maxDrawdownPct, worstDayPct, oversizedLosses, losingTrades: losers.length, trades: trades.length };
}

// LUMIR score = average of discipline, risk and consistency; a missing risk score is left out instead of counting as 0
export function lumirScore(discipline: number, risk: number | null, consistency: number): number {
  const parts = [discipline, consistency];
  if (risk !== null) parts.push(risk);
  return Math.round(parts.reduce((s, v) => s + v, 0) / parts.length);
}
